import { UseStepContext } from '@consigli/facade';
import { useProjectId, useServiceName } from '@consigli/hooks';
import { DocumentPackageType, ServiceName, PipelineName } from '@consigli/types';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { useCreateInsightPackage } from '@/features/package-creation/hooks/use-create-insight-package';
import { Route, ServiceIdIndexValue } from '@/routes';

import { CompareButtons } from './compare-buttons';
import { PACKAGE_TYPE, useCompareContext } from './use-compare-packages';
import { ExistingPackage } from '../secondary/existing-package';
import { NewPackage } from '../secondary/new-package';
import { SecondaryService } from '../secondary/secondary-service';
import { SecondaryTab } from '../secondary-tab';

interface ComparePackagesProps {
  setIsComparing: Dispatch<SetStateAction<boolean>>;
  setCurrentCounter: Dispatch<SetStateAction<number>>;
  setTargetCounter: Dispatch<SetStateAction<number>>;
  context: UseStepContext;
}

interface TabConfig {
  id: number;
  label: string;
  type: PACKAGE_TYPE;
  content: JSX.Element;
}

export const ComparePackages = ({
  setIsComparing,
  context,
  setCurrentCounter,
  setTargetCounter,
}: ComparePackagesProps) => {
  const projectId = useProjectId();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const serviceName = useServiceName();
  const type = ServiceIdIndexValue[serviceName] as DocumentPackageType;

  const {
    primaryData,
    primaryFiles,
    setSecondaryData,
    secondaryFiles,
    selectedComparisonPackage,
    compareServiceName,
    packageType,
  } = useCompareContext();

  const { createDocumentPackage, uploadFiles, comparePackages, startAnalysis } =
    useCreateInsightPackage({
      setCurrentCounter,
      setTargetCounter,
    });
  const { handleSubmit, getValues } = useFormContext<FieldValues>();

  const compareServiceId = useMemo(
    () => ServiceIdIndexValue[compareServiceName?.value as ServiceName],
    [compareServiceName?.value],
  );

  const handleExistingPackageComparison = useCallback(
    async (primaryPackageId: string) => {
      if (!selectedComparisonPackage) {
        throw new Error('No comparison package selected');
      }
      await uploadFiles(primaryFiles, primaryPackageId);
      return await comparePackages(primaryPackageId, selectedComparisonPackage.id);
    },
    [selectedComparisonPackage, uploadFiles, primaryFiles, comparePackages],
  );

  const handleNewPackageComparison = useCallback(
    async (primaryPackageId: string) => {
      if (!compareServiceId) {
        throw new Error('No comparison service selected');
      }

      const secondaryPackageId = await createDocumentPackage(
        compareServiceId,
        getValues('secondaryPackageName'),
        primaryData.language,
      );

      if (!secondaryPackageId) {
        throw new Error('Failed to create secondary package');
      }

      await uploadFiles(secondaryFiles, secondaryPackageId);
      await startAnalysis(secondaryPackageId, PipelineName.DEFAULT);
      await uploadFiles(primaryFiles, primaryPackageId);

      return await comparePackages(primaryPackageId, secondaryPackageId);
    },
    [
      compareServiceId,
      createDocumentPackage,
      getValues,
      primaryData.language,
      uploadFiles,
      secondaryFiles,
      startAnalysis,
      primaryFiles,
      comparePackages,
    ],
  );

  const onSubmit = useCallback(
    async (data: FieldValues, e?: React.BaseSyntheticEvent) => {
      try {
        e?.preventDefault();
        setSecondaryData(data);
        setIsComparing(true);

        const primaryPackageId = await createDocumentPackage(
          type,
          primaryData.iterationName,
          primaryData.language,
        );

        if (!primaryPackageId) {
          throw new Error('Failed to create primary package');
        }

        const response =
          packageType === PACKAGE_TYPE.EXISTING
            ? await handleExistingPackageComparison(primaryPackageId)
            : await handleNewPackageComparison(primaryPackageId);

        if (response && primaryPackageId) {
          toast.success(t('package-creation.success-analyzed'));
          navigate(
            `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${primaryPackageId}/${Route.DASHBOARD}`,
          );
        }
      } catch (error) {
        console.log('Error comparing packages:', error);
        toast.error(t('package-creation.package-compare-failed'));
      } finally {
        setIsComparing(false);
      }
    },
    [
      createDocumentPackage,
      primaryData,
      type,
      projectId,
      serviceName,
      navigate,
      setIsComparing,
      setSecondaryData,
      packageType,
      t,
      handleExistingPackageComparison,
      handleNewPackageComparison,
    ],
  );

  const tab = useCallback(
    (type: DocumentPackageType): TabConfig[] => [
      {
        id: 0,
        label: t('package-creation.compare-existing'),
        type: PACKAGE_TYPE.EXISTING,
        content: <ExistingPackage />,
      },
      {
        id: 1,
        label: t('package-creation.upload-new'),
        type: PACKAGE_TYPE.NEW,
        content: <NewPackage compareServiceId={type} />,
      },
    ],
    [t],
  );

  return (
    <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col" translate="no">
        <span className="mt-6 mb-4 flex flex-col">
          <SecondaryService />
          {compareServiceId && <SecondaryTab tabs={tab(compareServiceId)} />}
          <CompareButtons context={context} />
        </span>
      </div>
    </form>
  );
};
